/**
 * Convertidor mínimo de Markdown a elementos React.
 *
 * Por qué propio: la regla del proyecto es no sumar paquetes sin aprobación, y los docs usan un
 * subconjunto pequeño de Markdown. Seguridad: el resultado son ELEMENTOS React (el texto siempre
 * se escapa solo); nunca se usa dangerouslySetInnerHTML y el HTML crudo se muestra como texto.
 * Los enlaces pasan por `resolverEnlace`, que solo deja http(s), mailto y .md relativos.
 *
 * Soporta: títulos, párrafos, listas (con anidación por sangría), tablas, bloques de código
 * (incluido mermaid, que se muestra como código), citas, reglas, código en línea, enlaces,
 * negrita y cursiva.
 */
import { createElement, type ReactNode } from 'react';

export type EnlaceResuelto =
  | { tipo: 'externo'; href: string }
  | { tipo: 'interno'; ruta: string }
  | { tipo: 'ancla'; id: string }
  | { tipo: 'ninguno' };

/** Decide qué hacer con un enlace del Markdown. `rutaActual` es relativa a docs/ (p. ej. `guias/pruebas.md`). */
export function resolverEnlace(href: string, rutaActual: string): EnlaceResuelto {
  const limpio = href.trim();
  if (!limpio) return { tipo: 'ninguno' };
  // Esquema explícito: solo se aceptan http(s) y mailto.
  const esquema = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(limpio);
  if (esquema) {
    const nombre = (esquema[1] ?? '').toLowerCase();
    if (nombre === 'http' || nombre === 'https' || nombre === 'mailto') {
      return { tipo: 'externo', href: limpio };
    }
    return { tipo: 'ninguno' };
  }
  // «//dominio» o «\\dominio» serían enlaces externos disfrazados de relativos.
  if (/^[/\\]{2}/.test(limpio)) return { tipo: 'ninguno' };
  // Ancla dentro del mismo documento (los títulos llevan id, ver `idDeTitulo`).
  if (/^#[^\s#]+$/.test(limpio)) return { tipo: 'ancla', id: limpio.slice(1) };

  const [sinHash = ''] = limpio.split('#');
  const [sinConsulta = ''] = sinHash.split('?');
  if (!sinConsulta.toLowerCase().endsWith('.md')) return { tipo: 'ninguno' };

  const partes = rutaActual.split('/').slice(0, -1);
  for (const parte of sinConsulta.split('/')) {
    if (parte === '' || parte === '.') continue;
    if (parte === '..') {
      // Salirse de docs/ (hacia apps/, .claude/…) no es una página de la documentación.
      if (partes.length === 0) return { tipo: 'ninguno' };
      partes.pop();
    } else {
      partes.push(parte);
    }
  }
  return { tipo: 'interno', ruta: `/docs/${partes.join('/')}` };
}

export interface OpcionesMarkdown {
  /** Ruta del documento relativa a docs/, para resolver enlaces relativos. */
  rutaActual: string;
  /** Cómo dibujar un enlace interno; por defecto un `<a>` normal (la página usa `Link` del router). */
  enlaceInterno?: (ruta: string, hijos: ReactNode, clave: string) => ReactNode;
}

// ---------- Inline ----------

function inline(texto: string, opciones: OpcionesMarkdown, prefijo: string): ReactNode[] {
  const salida: ReactNode[] = [];
  let buffer = '';
  let n = 0;
  const volcar = () => {
    if (buffer) salida.push(buffer);
    buffer = '';
  };
  const clave = () => `${prefijo}-${n++}`;

  let i = 0;
  while (i < texto.length) {
    const c = texto[i] ?? '';

    if (c === '\\' && i + 1 < texto.length && /[\\`*_[\]()#|<>!-]/.test(texto[i + 1] ?? '')) {
      buffer += texto[i + 1];
      i += 2;
      continue;
    }

    if (c === '`') {
      const fin = texto.indexOf('`', i + 1);
      if (fin > i + 1) {
        volcar();
        salida.push(createElement('code', { key: clave() }, texto.slice(i + 1, fin)));
        i = fin + 1;
        continue;
      }
    }

    // Imagen: no se carga nada externo; se muestra solo el texto alternativo.
    if (c === '!' && texto[i + 1] === '[') {
      const finTexto = texto.indexOf('](', i + 2);
      const finUrl = finTexto >= 0 ? texto.indexOf(')', finTexto + 2) : -1;
      if (finUrl >= 0) {
        buffer += texto.slice(i + 2, finTexto);
        i = finUrl + 1;
        continue;
      }
    }

    if (c === '[') {
      const finTexto = texto.indexOf('](', i + 1);
      const finUrl = finTexto >= 0 ? texto.indexOf(')', finTexto + 2) : -1;
      if (finUrl >= 0) {
        volcar();
        const etiqueta = texto.slice(i + 1, finTexto);
        const href = texto.slice(finTexto + 2, finUrl).split(/\s+/)[0] ?? '';
        const k = clave();
        const hijos = inline(etiqueta, opciones, k);
        const destino = resolverEnlace(href, opciones.rutaActual);
        if (destino.tipo === 'externo') {
          salida.push(
            createElement(
              'a',
              { key: k, href: destino.href, rel: 'noopener noreferrer', target: '_blank' },
              ...hijos,
            ),
          );
        } else if (destino.tipo === 'ancla') {
          salida.push(createElement('a', { key: k, href: `#${destino.id}` }, ...hijos));
        } else if (destino.tipo === 'interno') {
          salida.push(
            opciones.enlaceInterno
              ? opciones.enlaceInterno(destino.ruta, hijos, k)
              : createElement('a', { key: k, href: destino.ruta }, ...hijos),
          );
        } else {
          // Sin destino válido: queda el texto, sin enlace.
          salida.push(createElement('span', { key: k }, ...hijos));
        }
        i = finUrl + 1;
        continue;
      }
    }

    if (c === '*' || c === '_') {
      const doble = texto[i + 1] === c;
      const marca = doble ? c + c : c;
      const inicio = i + marca.length;
      // «_» dentro de una palabra (snake_case) no es cursiva.
      const previo = texto[i - 1] ?? ' ';
      const valido = c === '*' || !/[A-Za-z0-9]/.test(previo);
      const fin = valido ? texto.indexOf(marca, inicio) : -1;
      if (fin > inicio && !/\s/.test(texto[inicio] ?? ' ')) {
        volcar();
        const k = clave();
        salida.push(
          createElement(
            doble ? 'strong' : 'em',
            { key: k },
            ...inline(texto.slice(inicio, fin), opciones, k),
          ),
        );
        i = fin + marca.length;
        continue;
      }
    }

    buffer += c;
    i++;
  }
  volcar();
  return salida;
}

// ---------- Bloques ----------

/** Id de un título al estilo GitHub (minúsculas, sin signos, espacios a guiones) para los enlaces `#ancla`. */
export function idDeTitulo(texto: string): string {
  return texto
    .toLowerCase()
    .replace(/[`*_]/g, '')
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .trim()
    .replace(/\s/g, '-');
}

const RE_FENCE = /^\s{0,3}(`{3,}|~{3,})\s*([\w-]*)/;
const RE_TITULO = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const RE_REGLA = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const RE_ITEM = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
const RE_SEPARADOR_TABLA = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

const empiezaTabla = (linea: string, siguiente: string | undefined): boolean =>
  linea.includes('|') &&
  siguiente !== undefined &&
  siguiente.includes('-') &&
  RE_SEPARADOR_TABLA.test(siguiente);

function celdas(linea: string): string[] {
  let t = linea.trim();
  if (t.startsWith('|')) t = t.slice(1);
  if (t.endsWith('|') && !t.endsWith('\\|')) t = t.slice(0, -1);
  // Respeta `\|` y los `|` dentro de código en línea.
  const resultado: string[] = [];
  let actual = '';
  let enCodigo = false;
  for (let i = 0; i < t.length; i++) {
    const c = t[i] ?? '';
    if (c === '\\' && t[i + 1] === '|') {
      actual += enCodigo ? '|' : '\\|';
      i++;
    } else if (c === '`') {
      enCodigo = !enCodigo;
      actual += c;
    } else if (c === '|' && !enCodigo) {
      resultado.push(actual.trim());
      actual = '';
    } else {
      actual += c;
    }
  }
  resultado.push(actual.trim());
  return resultado;
}

const esInicioDeBloque = (linea: string, siguiente: string | undefined): boolean =>
  RE_FENCE.test(linea) ||
  RE_TITULO.test(linea) ||
  RE_REGLA.test(linea) ||
  RE_ITEM.test(linea) ||
  /^\s{0,3}>/.test(linea) ||
  empiezaTabla(linea, siguiente);

interface LineaLista {
  sangria: number;
  ordenada: boolean;
  texto: string;
}

interface ItemLista {
  texto: string;
  hijos: ListaArmada | null;
}

interface ListaArmada {
  ordenada: boolean;
  items: ItemLista[];
}

/** Arma el árbol de una lista; la sangría mayor que la del ítem padre significa «anidado». */
function construirLista(
  lineas: LineaLista[],
  desde: number,
  sangriaBase: number,
): ListaArmada & { siguiente: number } {
  const items: ItemLista[] = [];
  const ordenada = lineas[desde]?.ordenada ?? false;
  let i = desde;
  while (i < lineas.length) {
    const actual = lineas[i];
    if (!actual || actual.sangria < sangriaBase) break;
    if (actual.sangria > sangriaBase) {
      const padre = items[items.length - 1];
      const sub = construirLista(lineas, i, actual.sangria);
      // Con sangrías no monótonas (0, 4, 2) el padre ya puede tener hijos: se fusionan.
      if (padre?.hijos) padre.hijos.items.push(...sub.items);
      else if (padre) padre.hijos = { ordenada: sub.ordenada, items: sub.items };
      else items.push(...sub.items);
      i = sub.siguiente;
      continue;
    }
    items.push({ texto: actual.texto, hijos: null });
    i++;
  }
  return { ordenada, items, siguiente: i };
}

function dibujarLista(lista: ListaArmada, opciones: OpcionesMarkdown, prefijo: string): ReactNode {
  return createElement(
    lista.ordenada ? 'ol' : 'ul',
    { key: prefijo },
    ...lista.items.map((item, n) => {
      const k = `${prefijo}-${n}`;
      return createElement(
        'li',
        { key: k },
        ...inline(item.texto, opciones, k),
        item.hijos ? dibujarLista(item.hijos, opciones, `${k}l`) : null,
      );
    }),
  );
}

function dibujarTabla(
  encabezado: string[],
  filas: string[][],
  opciones: OpcionesMarkdown,
  k: string,
): ReactNode {
  return createElement(
    'div',
    { key: k, className: 'tabla-contenedor' },
    createElement(
      'table',
      { className: 'tabla' },
      createElement(
        'thead',
        null,
        createElement(
          'tr',
          null,
          ...encabezado.map((c, n) =>
            createElement('th', { key: n }, ...inline(c, opciones, `${k}h${n}`)),
          ),
        ),
      ),
      createElement(
        'tbody',
        null,
        ...filas.map((fila, f) =>
          createElement(
            'tr',
            { key: f },
            ...encabezado.map((_, n) =>
              createElement('td', { key: n }, ...inline(fila[n] ?? '', opciones, `${k}f${f}c${n}`)),
            ),
          ),
        ),
      ),
    ),
  );
}

function bloques(lineas: string[], opciones: OpcionesMarkdown, prefijo: string): ReactNode[] {
  const salida: ReactNode[] = [];
  let i = 0;
  while (i < lineas.length) {
    const linea = lineas[i] ?? '';
    const k = `${prefijo}-${i}`;

    if (linea.trim() === '') {
      i++;
      continue;
    }

    const fence = RE_FENCE.exec(linea);
    if (fence) {
      const marca = fence[1] ?? '```';
      const lenguaje = fence[2] ?? '';
      const codigo: string[] = [];
      i++;
      while (i < lineas.length && !(lineas[i] ?? '').trim().startsWith(marca)) {
        codigo.push(lineas[i] ?? '');
        i++;
      }
      i++; // cierre (o fin del texto si quedó abierto)
      salida.push(
        createElement(
          'pre',
          { key: k, 'data-lenguaje': lenguaje || undefined },
          createElement('code', null, codigo.join('\n')),
        ),
      );
      continue;
    }

    const titulo = RE_TITULO.exec(linea);
    if (titulo) {
      const nivel = Math.min((titulo[1] ?? '#').length, 6);
      salida.push(
        createElement(
          `h${nivel}`,
          { key: k, id: idDeTitulo(titulo[2] ?? '') || undefined },
          ...inline(titulo[2] ?? '', opciones, k),
        ),
      );
      i++;
      continue;
    }

    if (RE_REGLA.test(linea)) {
      salida.push(createElement('hr', { key: k }));
      i++;
      continue;
    }

    if (/^\s{0,3}>/.test(linea)) {
      const cita: string[] = [];
      while (i < lineas.length && /^\s{0,3}>/.test(lineas[i] ?? '')) {
        cita.push((lineas[i] ?? '').replace(/^\s{0,3}>\s?/, ''));
        i++;
      }
      salida.push(createElement('blockquote', { key: k }, ...bloques(cita, opciones, k)));
      continue;
    }

    if (empiezaTabla(linea, lineas[i + 1])) {
      const encabezado = celdas(linea);
      i += 2;
      const filas: string[][] = [];
      while (
        i < lineas.length &&
        (lineas[i] ?? '').trim() !== '' &&
        (lineas[i] ?? '').includes('|')
      ) {
        filas.push(celdas(lineas[i] ?? ''));
        i++;
      }
      salida.push(dibujarTabla(encabezado, filas, opciones, k));
      continue;
    }

    if (RE_ITEM.test(linea)) {
      const items: LineaLista[] = [];
      while (i < lineas.length) {
        const l = lineas[i] ?? '';
        const m = RE_ITEM.exec(l);
        if (m) {
          items.push({
            sangria: (m[1] ?? '').replace(/\t/g, '    ').length,
            ordenada: /\d/.test(m[2] ?? ''),
            texto: m[3] ?? '',
          });
          i++;
        } else if (l.trim() !== '' && /^\s+/.test(l) && items.length > 0) {
          // Continuación del ítem anterior (línea sangrada).
          const ultimo = items[items.length - 1];
          if (ultimo) ultimo.texto += ` ${l.trim()}`;
          i++;
        } else {
          break;
        }
      }
      const base = Math.min(...items.map((x) => x.sangria));
      salida.push(dibujarLista(construirLista(items, 0, base), opciones, k));
      continue;
    }

    // Párrafo: hasta una línea en blanco o el inicio de otro bloque.
    const parrafo: string[] = [linea.trim()];
    i++;
    while (
      i < lineas.length &&
      (lineas[i] ?? '').trim() !== '' &&
      !esInicioDeBloque(lineas[i] ?? '', lineas[i + 1])
    ) {
      parrafo.push((lineas[i] ?? '').trim());
      i++;
    }
    salida.push(createElement('p', { key: k }, ...inline(parrafo.join(' '), opciones, k)));
  }
  return salida;
}

export function markdownAElementos(texto: string, opciones: OpcionesMarkdown): ReactNode[] {
  return bloques(texto.replace(/\r\n?/g, '\n').split('\n'), opciones, 'md');
}
